import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Ban, Info } from 'lucide-react'

import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { customerExposureQueryOptions } from '@/features/customers/queries'

/**
 * What this member already owes, across every branch.
 *
 * Computed over the whole institution deliberately: a member owing at one branch and applying at
 * another is the case this exists to catch, so it is never filtered to the branch the officer is
 * sitting in. It sits above the application and the approval for the same reason the same banner
 * sits above a customer file — it is the finding the decision turns on, and it only works if it is
 * read before the decision rather than found afterwards.
 *
 * A blacklisted member is stated plainly and separately. The server refuses the lending
 * (`loans.customer_not_eligible`); this is so nobody gets that far having already told the member
 * a figure.
 */
export function LoanExposureBanner({ customerId }: { customerId: string }) {
  const query = useQuery({
    ...customerExposureQueryOptions(customerId),
    enabled: customerId !== '',
  })

  if (query.isPending) {
    return <Skeleton className="h-16 w-full" />
  }

  // A failure here is not the application's problem to solve — the customer may simply be at a
  // branch this user cannot read. It is said once, quietly, rather than blocking the form.
  if (query.isError) {
    return (
      <Alert tone="warning">
        <AlertTriangle aria-hidden />
        <div className="space-y-1">
          <AlertTitle>The member&apos;s cross-branch exposure could not be read</AlertTitle>
          <AlertDescription>
            This says nothing about whether they owe elsewhere. Do not read it as "nothing
            outstanding".
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  const exposure = query.data

  if (exposure.isBlacklisted) {
    return (
      <Alert tone="danger">
        <Ban aria-hidden />
        <div className="space-y-1">
          <AlertTitle>This member is blacklisted</AlertTitle>
          <AlertDescription>
            {exposure.blacklistReason ?? 'No reason was recorded against the listing.'} The server
            refuses to lend to them, so nothing keyed below can complete.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  const behind = exposure.daysInArrears > 0

  return (
    <Alert tone={behind ? 'warning' : 'info'}>
      {behind ? <AlertTriangle aria-hidden /> : <Info aria-hidden />}
      <div className="space-y-1">
        <AlertTitle>
          {exposure.openLoanCount === 0
            ? 'No other loans anywhere in the institution'
            : `${exposure.openLoanCount} open loan${exposure.openLoanCount === 1 ? '' : 's'} across the institution`}
        </AlertTitle>
        <AlertDescription>
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-1.5">
              Outstanding
              <MoneyDisplay
                value={exposure.totalOutstandingMinor}
                currency={exposure.currencyCode}
                className="inline"
              />
            </span>
            {behind ? (
              <span className="text-danger-text">
                {exposure.daysInArrears} days in arrears somewhere
              </span>
            ) : null}
            {exposure.branchesWithExposure.length > 0 ? (
              <span>
                At {exposure.branchesWithExposure.join(', ')} — computed over every branch, not
                only this one.
              </span>
            ) : null}
          </span>
        </AlertDescription>
      </div>
    </Alert>
  )
}
